import { Checkbox, Dialog, Typography } from '@frontend/components/ui';
import {
  useGetProjectID,
  useProjectPermission,
} from '@frontend/editor/data-access';
import { useGetProject } from '@frontend/sorghum/data-access';
import { UICtx } from '@frontend/sorghum/external-providers';
import { ModalTypesEnum, SocialTypeEnum } from '@frontend/sorghum/interface';
import {
  VIEWER_PERMISSION_EN,
  VIEWER_PERMISSION_ZH,
  getViewerModalStatus,
  setViewerModalStatus,
} from '@frontend/sorghum/utils';
import { Box, Link } from '@mui/material';
import { styled } from '@mui/system';
import { useContext, useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

const ContentContainerStyled = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '34px',
}));

interface ViewerModeModalProps {
  socialType: SocialTypeEnum;
}

export function ViewerModeModal({ socialType }: ViewerModeModalProps) {
  const [t, i18next] = useTranslation();
  const { modalType, closeModal, openModal } = useContext(UICtx);
  const [isCheck, setIsCheck] = useState(false);
  const { data: projectID } = useGetProjectID();
  const { data: project } = useGetProject(projectID);

  // const [isLoaded, setIsLoaded] = useState(false);
  const { isViewer, isPermissionViewer } = useProjectPermission();

  const isLocalStorageChecked = useMemo(() => {
    if (projectID) {
      return getViewerModalStatus({ projectID });
    }
    return false;
  }, [projectID]);

  const viewerLearnMoreUrl =
    i18next.language === 'zh_tw' ? VIEWER_PERMISSION_ZH : VIEWER_PERMISSION_EN;

  useEffect(() => {
    // 如果已經 loaded 或 isLocalStorageChecked 為 true 時 ，直接 return
    if (isLocalStorageChecked) return;

    // socialType 為 facebook 且 step 等於 1 (beta end) 或 step 等於 2 (trial end) 時
    // 不會顯示此 modal
    if (
      socialType === SocialTypeEnum.FACEBOOK
        ? projectID && project?.step === 3 && isViewer
        : isPermissionViewer
    ) {
      openModal(ModalTypesEnum.VIEWER_MODE);
    }
  }, [
    isLocalStorageChecked,
    socialType,
    projectID,
    project?.step,
    isViewer,
    isPermissionViewer,
    openModal,
  ]);

  return (
    <Dialog
      open={modalType === ModalTypesEnum.VIEWER_MODE}
      size="xs"
      title={t('dashboard.viewerModal.title')}
      handleClose={() => null}
      handleConfirm={() => {
        closeModal();
        if (projectID) {
          setViewerModalStatus({ projectID, checked: isCheck });
        }
      }}
      confirmBtnText={t('dashboard.viewerModal.button')}
      uppercase={false}
      closeBtn={false}
    >
      <ContentContainerStyled>
        <Typography variant="body1" color="grey.800">
          <Trans i18nKey="dashboard.viewerModal.content">
            You are in the Viewer Mode, any edit will not be saved in the
            system. Check to learn more.
            <Link
              sx={{ color: 'info.main' }}
              onClick={() => window.open(viewerLearnMoreUrl, '_blank')}
            >
              {t('dashboard.viewerModal.link')}
            </Link>
          </Trans>
        </Typography>

        <Checkbox
          variant="body2"
          color="primary"
          fontWeight={400}
          labelColor="grey.700"
          label={t('dashboard.viewerModal.checkbox')}
          checked={isCheck}
          onChange={(e) => setIsCheck(e.target.checked)}
        />
      </ContentContainerStyled>
    </Dialog>
  );
}

export default ViewerModeModal;
